<template>
  <div class="cbl-shell">
    <!-- Error dialog -->
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>

    <!-- Toast -->
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="cbl-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <!-- Signature Debug Modal (custom teleported — matches modern pages) -->
    <teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="signatureDebug"
          class="cbl-modal-backdrop"
          @click.self="closeSignatureDebug"
        >
          <div class="cbl-modal cbl-modal-md">
            <div class="cbl-modal-head">
              <div>
                <div class="cbl-modal-eyebrow">Debug</div>
                <div class="cbl-modal-title">Signature Debug</div>
              </div>
              <button
                type="button"
                class="cbl-modal-close"
                @click="closeSignatureDebug"
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" />
                </svg>
              </button>
            </div>

            <div class="cbl-modal-body">
              <!-- Signed status pill up top for at-a-glance -->
              <div class="sig-status-row">
                <span
                  v-if="signatureDebug.signed === 1 || signatureDebug.signed === true"
                  class="pill pill-success pill-lg"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
                  Signed — signature attached
                </span>
                <span v-else class="pill pill-neutral pill-lg">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg>
                  Unsigned — no client secret at send time
                </span>
              </div>

              <!-- Three header rows -->
              <div class="sig-fields">
                <div class="sig-field">
                  <div class="sig-field-head">
                    <span class="sig-field-name">Request ID</span>
                    <code class="sig-field-header">X-IndiaPayU-Request-Id</code>
                  </div>
                  <div class="sig-field-value">
                    <span class="mono-value">{{ signatureDebug.request_id || "—" }}</span>
                    <button
                      v-if="signatureDebug.request_id"
                      type="button"
                      class="mini-copy"
                      @click="copyToClipboard(signatureDebug.request_id)"
                      title="Copy"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke-linecap="round" /></svg>
                    </button>
                  </div>
                </div>

                <div class="sig-field">
                  <div class="sig-field-head">
                    <span class="sig-field-name">Timestamp</span>
                    <code class="sig-field-header">X-IndiaPayU-Timestamp · epoch seconds</code>
                  </div>
                  <div class="sig-field-value">
                    <span class="mono-value">{{ signatureDebug.signed_at_epoch || "—" }}</span>
                  </div>
                </div>

                <div class="sig-field">
                  <div class="sig-field-head">
                    <span class="sig-field-name">Signature</span>
                    <code class="sig-field-header">X-IndiaPayU-Signature · sha256=…</code>
                  </div>
                  <div class="sig-field-value">
                    <span class="mono-value">{{ signatureDebug.signature ? ("sha256=" + signatureDebug.signature) : "—" }}</span>
                    <button
                      v-if="signatureDebug.signature"
                      type="button"
                      class="mini-copy"
                      @click="copyToClipboard('sha256=' + signatureDebug.signature)"
                      title="Copy"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke-linecap="round" /></svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Signed string (the input to HMAC) -->
              <div class="sig-block-full">
                <div class="sig-block-head">
                  <span class="sig-block-title">Exact signed string</span>
                  <span class="sig-block-hint">what we ran HMAC-SHA256 over</span>
                  <button
                    type="button"
                    class="mini-copy mini-copy-inline"
                    @click="copyToClipboard(signedStringFor(signatureDebug))"
                    title="Copy signed string"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke-linecap="round" /></svg>
                    Copy
                  </button>
                </div>
                <textarea
                  readonly
                  class="sig-signed-string"
                  v-text="signedStringFor(signatureDebug)"
                ></textarea>
                <p class="sig-verify-hint">
                  Client should recompute <code>HMAC_SHA256(client_secret, ABOVE)</code> and compare to the signature above.
                </p>
              </div>
            </div>

            <div class="cbl-modal-foot">
              <button type="button" class="btn btn-ghost" @click="closeSignatureDebug">Close</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Detail Modal -->
    <base-modal v-if="detailLog" :title="detailTitle" @close="closeDetail" height="">
      <div class="detail-modal">
        <!-- Top summary strip -->
        <div class="detail-grid">
          <div class="detail-cell">
            <div class="cell-label">Status</div>
            <div class="cell-value">
              <span class="pill" :class="statusPillClass(detailLog.status)">{{ detailLog.status }}</span>
            </div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">HTTP Code</div>
            <div class="cell-value cell-strong">{{ detailLog.response_status || "—" }}</div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Type</div>
            <div class="cell-value">
              <span class="pill pill-type">{{ detailLog.callback_type || "—" }}</span>
            </div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Attempt</div>
            <div class="cell-value cell-strong">{{ detailLog.attempt_number || 1 }} of 3</div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Client</div>
            <div class="cell-value">{{ detailLog.client_name || "—" }}</div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Triggered By</div>
            <div class="cell-value">{{ detailLog.triggered_by || "—" }}</div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Sent At (IST)</div>
            <div class="cell-value">{{ formatDate(detailLog.created_at) }}</div>
          </div>
          <div class="detail-cell">
            <div class="cell-label">Signed?</div>
            <div class="cell-value">
              <span
                class="pill"
                :class="detailLog.signed === 1 || detailLog.signed === true ? 'pill-success' : 'pill-neutral'"
              >{{ detailLog.signed === 1 || detailLog.signed === true ? "Yes" : "No" }}</span>
            </div>
          </div>
        </div>

        <!-- IDs -->
        <div class="detail-section">
          <div class="detail-grid detail-grid-2">
            <div class="detail-cell">
              <div class="cell-label">Ref ID</div>
              <div class="cell-mono">{{ detailLog.ref_id || "—" }}</div>
            </div>
            <div class="detail-cell">
              <div class="cell-label">Request ID</div>
              <div class="cell-mono">{{ detailLog.request_id || "—" }}</div>
            </div>
            <div class="detail-cell">
              <div class="cell-label">Merchant Order ID</div>
              <div class="cell-mono">{{ detailLog.merchant_order_id || "—" }}</div>
            </div>
            <div class="detail-cell">
              <div class="cell-label">Receipt ID</div>
              <div class="cell-mono">{{ detailLog.receipt_id || "—" }}</div>
            </div>
          </div>
        </div>

        <!-- URL -->
        <div class="detail-section">
          <div class="cell-label">Callback URL</div>
          <div class="cell-mono cell-mono-block">{{ detailLog.callback_url || "—" }}</div>
        </div>

        <!-- Request payload -->
        <div class="detail-section">
          <div class="detail-section-head">
            <div class="cell-label">Request Payload</div>
            <button type="button" class="link-btn" @click="copyToClipboard(prettyJson(detailLog.request_payload))">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke-linecap="round" /></svg>
              Copy
            </button>
          </div>
          <pre class="code-block">{{ prettyJson(detailLog.request_payload) }}</pre>
        </div>

        <!-- Response body -->
        <div class="detail-section">
          <div class="detail-section-head">
            <div class="cell-label">Response Body</div>
            <button type="button" class="link-btn" @click="copyToClipboard(prettyJson(detailLog.response_body))">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 012-2h10" stroke-linecap="round" /></svg>
              Copy
            </button>
          </div>
          <pre class="code-block">{{ prettyJson(detailLog.response_body) }}</pre>
        </div>

        <!-- Error message -->
        <div v-if="detailLog.error_message" class="detail-section">
          <div class="cell-label">Error Message</div>
          <pre class="code-block code-block-error">{{ detailLog.error_message }}</pre>
        </div>

        <!-- Signature block -->
        <div v-if="detailLog.request_id" class="detail-section">
          <div class="cell-label">Outbound signature headers</div>
          <div class="sig-block">
            <div><span class="sig-key">X-IndiaPayU-Timestamp:</span> {{ detailLog.signed_at_epoch || "—" }}</div>
            <div><span class="sig-key">X-IndiaPayU-Request-Id:</span> {{ detailLog.request_id }}</div>
            <div class="break-all">
              <span class="sig-key">X-IndiaPayU-Signature:</span>
              {{ detailLog.signature ? ("sha256=" + detailLog.signature) : "—" }}
            </div>
          </div>
          <button
            v-if="detailLog.signature"
            type="button"
            class="link-btn"
            @click="openSignatureDebug(detailLog)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" stroke-linecap="round" /></svg>
            View signature debug string
          </button>
        </div>

        <div class="detail-foot">
          <button type="button" class="btn btn-ghost" @click="closeDetail">Close</button>
          <button
            v-if="can('callback.resend')"
            type="button"
            class="btn btn-primary"
            @click="openResendDialog(detailLog); closeDetail()"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon"><path d="M4 4v6h6M20 20v-6h-6" stroke-linecap="round" stroke-linejoin="round" /><path d="M4 10a8 8 0 0113.66-4.36L20 8M20 14a8 8 0 01-13.66 4.36L4 16" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Resend Callback
          </button>
        </div>
      </div>
    </base-modal>

    <!-- Resend Dialog -->
    <base-dialog :show="showResendDialog" title="Resend Callback" @close="closeResendDialog">
      <div class="modal-body-inline">
        <div class="kv">
          <div class="kv-label">Callback URL</div>
          <input v-model="resendForm.callbackUrl" type="text" class="cbl-input" placeholder="https://example.com/callback" />
        </div>
        <div class="kv">
          <div class="kv-label">Request Payload (JSON)</div>
          <textarea
            v-model="resendForm.payloadText"
            class="kv-textarea kv-textarea-lg"
            placeholder='{"type": "payin", ...}'
          ></textarea>
          <p v-if="resendForm.payloadError" class="kv-error">{{ resendForm.payloadError }}</p>
        </div>
        <div v-if="resendForm.responseStatus" class="kv">
          <div class="kv-label">Response</div>
          <div class="resp-block">
            <div class="resp-status">Status: <b>{{ resendForm.responseStatus }}</b></div>
            <pre class="code-block">{{ resendForm.responseBody }}</pre>
          </div>
        </div>
        <div class="detail-foot">
          <button type="button" class="btn btn-ghost" @click="closeResendDialog">Close</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="resendForm.isSending"
            @click="submitResend"
          >
            <span v-if="resendForm.isSending" class="btn-spinner"></span>
            {{ resendForm.isSending ? "Sending…" : "Send" }}
          </button>
        </div>
      </div>
    </base-dialog>

    <!-- Header -->
    <header class="cbl-header">
      <div>
        <div class="eyebrow">Ops</div>
        <h1>Callback Logs</h1>
        <p class="subtitle">Every webhook we've fired — request payload, response body, signatures, retries.</p>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn btn-ghost btn-sm"
          title="Refresh"
          @click="fetchLogs"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke-linecap="round" />
            <path d="M20 4v5h-5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Refresh
        </button>
        <button
          type="button"
          class="btn btn-export"
          :disabled="isExporting"
          @click="exportData"
        >
          <svg v-if="!isExporting" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="btn-icon">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span v-else class="btn-spinner btn-spinner-dark"></span>
          {{ isExporting ? "Exporting…" : "Export to Excel" }}
        </button>
      </div>
    </header>

    <!-- Filter bar -->
    <section class="cbl-filters">
      <div class="search-wrap">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
        </svg>
        <input
          v-model.trim="searchKeyWord"
          type="text"
          placeholder="Search Ref ID, Request ID, Order ID, Receipt ID, Client…"
          class="search-input"
        />
        <button
          v-if="searchKeyWord"
          type="button"
          class="search-clear"
          @click="searchKeyWord = ''"
          title="Clear"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="chip-group">
        <button
          v-for="s in quickStatuses"
          :key="s.value"
          type="button"
          class="chip"
          :class="[
            statusFilter === s.value && 'chip-active',
            statusFilter === s.value && ('chip-active-' + (s.value || 'all')),
          ]"
          @click="statusFilter = s.value"
        >{{ s.label }}</button>
      </div>

      <button
        type="button"
        class="chip chip-advanced"
        :class="{ 'chip-active chip-active-primary': showSearch }"
        @click="toggleSearch"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chip-icon">
          <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        Filters
      </button>
    </section>

    <!-- Advanced Filters (expandable) -->
    <transition name="fade">
      <section v-if="showSearch" class="cbl-advanced">
        <div class="adv-head">
          <div class="adv-title">Advanced filters</div>
          <button type="button" class="link-btn link-btn-danger" @click="clearFilters">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" /></svg>
            Clear all
          </button>
        </div>
        <div class="adv-grid">
          <div class="cbl-field">
            <label>Ref ID</label>
            <input v-model.trim="refIdSearch" type="text" placeholder="Ref ID" class="cbl-input" />
          </div>
          <div class="cbl-field">
            <label>Request ID</label>
            <input v-model.trim="requestIdSearch" type="text" placeholder="Request ID" class="cbl-input" />
          </div>
          <div class="cbl-field">
            <label>Merchant Order ID</label>
            <input v-model.trim="merchantOrderIdSearch" type="text" placeholder="Order ID" class="cbl-input" />
          </div>
          <div class="cbl-field">
            <label>Receipt ID</label>
            <input v-model.trim="receiptIdSearch" type="text" placeholder="Receipt ID" class="cbl-input" />
          </div>
          <div class="cbl-field">
            <label>Client</label>
            <select v-model="clientNameFilter" class="cbl-input">
              <option value="">All Clients</option>
              <option v-for="c in clients" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="cbl-field">
            <label>Type</label>
            <select v-model="typeFilter" class="cbl-input">
              <option value="">All</option>
              <option value="payin">Pay-In</option>
              <option value="payout">Pay-Out</option>
              <option value="failed_order">Failed Order</option>
              <option value="generic">Generic</option>
            </select>
          </div>
          <div class="cbl-field">
            <label>Triggered By</label>
            <select v-model="triggeredByFilter" class="cbl-input">
              <option value="">All Sources</option>
              <option value="callbackHook">Manual Approval (callbackHook)</option>
              <option value="autoCallbackHook">Auto Approval (autoCallbackHook)</option>
              <option value="payOutCallbackHook">Payout Approval</option>
              <option value="callbackHandler">Callback Handler</option>
              <option value="manual_resend">Manual Resend</option>
              <option value="markPayInFailed">Mark Pay-In Failed</option>
              <option value="wallet_lock">Wallet Lock</option>
              <option value="bank_data_cron">Bank-Data Cron</option>
            </select>
          </div>
          <div class="cbl-field">
            <label>Signed?</label>
            <select v-model="signedFilter" class="cbl-input">
              <option value="">All</option>
              <option value="1">Yes (signature attached)</option>
              <option value="0">No (unsigned)</option>
            </select>
          </div>
        </div>
        <div class="adv-dates">
          <label class="date-pill">
            <span class="date-pill-label">From</span>
            <input type="date" v-model.trim="startDate" class="date-pill-input" />
          </label>
          <label class="date-pill">
            <span class="date-pill-label">To</span>
            <input type="date" v-model.trim="endDate" class="date-pill-input" />
          </label>
        </div>
      </section>
    </transition>

    <!-- Table -->
    <section class="cbl-card">
      <div class="card-head">
        <div class="card-title">
          Logs
          <span class="head-count">{{ paginatorInfo.total || 0 }}</span>
        </div>
        <div v-if="paginatorInfo.total > 0" class="card-pag-info">
          Showing {{ paginatorInfo.firstItem }}–{{ paginatorInfo.lastItem }} of {{ paginatorInfo.total }}
        </div>
      </div>

      <div v-if="isLoading" class="cbl-loading">
        <div class="loading-spinner"></div>
        <div class="loading-label">Loading callback logs…</div>
      </div>

      <div v-else-if="!hasLogs" class="empty-hero">
        <div class="empty-hero-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M22 4L12 14.01l-3-3" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="empty-hero-title">No callback logs found</div>
        <div class="empty-hero-sub">Try clearing filters or adjusting the date range.</div>
      </div>

      <div v-else class="table-wrap">
        <table class="cbl-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Order ID</th>
              <th>Receipt ID</th>
              <th>Client</th>
              <th>Type</th>
              <th>Status</th>
              <th>Attempt</th>
              <th>Triggered by</th>
              <th>Sent at (IST)</th>
              <th>Request ID</th>
              <th>Signed</th>
              <th>URL</th>
              <th>HTTP</th>
              <th class="th-actions">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in logs"
              :key="item.id || index"
              class="clickable-row"
              @click="openDetail(item)"
            >
              <td class="td-num td-mini">{{ paginatorInfo.firstItem + index }}</td>
              <td class="td-mono">{{ item.merchant_order_id || "—" }}</td>
              <td class="td-mono">{{ item.receipt_id || "—" }}</td>
              <td class="td-mini">{{ item.client_name || "—" }}</td>
              <td>
                <span class="pill pill-type">{{ item.callback_type }}</span>
              </td>
              <td>
                <span class="pill" :class="statusPillClass(item.status)">{{ item.status }}</span>
              </td>
              <td class="td-mini">{{ item.attempt_number }}/3</td>
              <td class="td-mini">{{ item.triggered_by || "—" }}</td>
              <td class="td-mini">{{ formatDate(item.created_at) }}</td>
              <td class="td-mono">
                <span
                  v-if="item.request_id"
                  class="mono-link"
                  title="View signature details"
                  @click.stop="openSignatureDebug(item)"
                >{{ truncate(item.request_id, 12) }}</span>
                <span v-else>—</span>
              </td>
              <td>
                <span
                  v-if="item.signed === 1 || item.signed === true"
                  class="signed-badge signed-yes"
                  title="Signed"
                >✓</span>
                <span v-else class="signed-badge signed-no" title="Unsigned">✗</span>
              </td>
              <td class="td-mono td-clip">{{ truncate(item.callback_url, 35) }}</td>
              <td class="td-http">{{ item.response_status || "—" }}</td>
              <td class="td-actions" @click.stop>
                <button
                  type="button"
                  class="action-btn action-resend"
                  @click.stop="openResendDialog(item)"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M4 4v6h6M20 20v-6h-6" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4 10a8 8 0 0113.66-4.36L20 8M20 14a8 8 0 01-13.66 4.36L4 16" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Resend
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isLoading && hasLogs" class="pag-bar">
        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
      </div>
    </section>
  </div>
</template>

<script>
import http from "../../http-common";
import Pagination from "../../components/shared/pagination.vue";
import moment from "moment-timezone";

// Debounce timer for text inputs — avoid firing a fetch on every keystroke.
const DEBOUNCE_MS = 300;
// Hard cap the client-side XLSX export at the backend's own ceiling so
// the two agree on the max envelope. Body cells (request/response) are
// separately truncated to 8KB to keep the file size sensible.
const EXPORT_CAP = 5000;
const EXPORT_CELL_MAX = 8192;

export default {
  name: "callback-logs",
  components: { Pagination },
  data() {
    return {
      isLoading: false,
      loadSeq: 0,
      isExporting: false,
      error: null,
      baseToast: { type: "", message: "" },

      logs: [],
      paginatorInfo: {
        currentPage: 1,
        lastPage: 1,
        firstItem: 0,
        lastItem: 0,
        total: 0,
        hasMorePages: false,
      },
      page: 1,
      first: 20,

      showSearch: false,
      searchKeyWord: "",
      refIdSearch: "",
      requestIdSearch: "",
      merchantOrderIdSearch: "",
      receiptIdSearch: "",
      clientNameFilter: "",
      typeFilter: "",
      triggeredByFilter: "",
      signedFilter: "",
      statusFilter: "",
      startDate: "",
      endDate: "",

      // Populated on created(). Empty list is fine — clients dropdown just
      // shows "All Clients" as the only option.
      clients: [],

      // Row detail modal target. Non-null → modal open.
      detailLog: null,

      // Signature debug modal target.
      signatureDebug: null,

      showResendDialog: false,
      resendForm: {
        logId: null,
        callbackUrl: "",
        payloadText: "",
        payloadError: null,
        responseStatus: null,
        responseBody: null,
        isSending: false,
      },

      // Quick-filter chips in the header. Colors applied per-status when active.
      quickStatuses: [
        { value: "", label: "All" },
        { value: "success", label: "Success" },
        { value: "failed", label: "Failed" },
        { value: "pending", label: "Pending" },
      ],

      searchDebounceTimer: null,
    };
  },
  computed: {
    hasLogs() {
      return this.logs && this.logs.length > 0;
    },
    detailTitle() {
      if (!this.detailLog) return "";
      return `Callback #${this.detailLog.id} — ${this.detailLog.merchant_order_id || this.detailLog.ref_id || ""}`;
    },
  },
  watch: {
    page() { this.fetchLogs(); },
    first() { this.page = 1; this.fetchLogs(); },
    // Text inputs → debounced.
    searchKeyWord() { this.debouncedFetch(); },
    refIdSearch() { this.debouncedFetch(); },
    requestIdSearch() { this.debouncedFetch(); },
    merchantOrderIdSearch() { this.debouncedFetch(); },
    receiptIdSearch() { this.debouncedFetch(); },
    // Dropdowns → immediate.
    clientNameFilter() { this.page = 1; this.fetchLogs(); },
    typeFilter() { this.page = 1; this.fetchLogs(); },
    triggeredByFilter() { this.page = 1; this.fetchLogs(); },
    signedFilter() { this.page = 1; this.fetchLogs(); },
    statusFilter() { this.page = 1; this.fetchLogs(); },
    startDate() { this.page = 1; this.fetchLogs(); },
    endDate() { this.page = 1; this.fetchLogs(); },
  },
  created() {
    this.fetchLogs();
    this.loadClients();
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    setPage(page) { this.page = page; },
    toggleSearch() { this.showSearch = !this.showSearch; },

    // Load clients for the dropdown. Vendor-scoped by the backend, so
    // any authenticated caller only sees their own vendor's clients.
    // Fire-and-forget: on failure the dropdown just stays empty.
    async loadClients() {
      try {
        const resp = await http.get("/clients/getClientName?first=200");
        const rows = (resp.data && resp.data.clients) || [];
        const names = new Set();
        for (const r of rows) if (r && r.clientName) names.add(r.clientName);
        this.clients = Array.from(names).sort();
      } catch (e) { /* dropdown stays empty */ }
    },

    debouncedFetch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.fetchLogs();
      }, DEBOUNCE_MS);
    },

    // Build the URLSearchParams the backend accepts. Shared by fetchLogs
    // and exportData so filter semantics stay identical between the
    // on-screen list and the export file.
    buildQuery(overrides = {}) {
      const qs = new URLSearchParams();
      qs.set("page", String(overrides.page != null ? overrides.page : this.page));
      qs.set("first", String(overrides.first != null ? overrides.first : this.first));

      if (this.searchKeyWord) qs.set("searchKeyWord", this.searchKeyWord);
      if (this.refIdSearch) qs.set("ref_id", this.refIdSearch);
      if (this.requestIdSearch) qs.set("request_id", this.requestIdSearch);
      if (this.merchantOrderIdSearch) qs.set("merchant_order_id", this.merchantOrderIdSearch);
      if (this.receiptIdSearch) qs.set("receipt_id", this.receiptIdSearch);
      if (this.clientNameFilter) qs.set("client_name", this.clientNameFilter);
      if (this.typeFilter) qs.set("callback_type", this.typeFilter);
      if (this.triggeredByFilter) qs.set("triggered_by", this.triggeredByFilter);
      if (this.signedFilter !== "") qs.set("signed", this.signedFilter);
      if (this.statusFilter) qs.set("status", this.statusFilter);
      if (this.startDate) qs.set("startDate", this.startDate);
      if (this.endDate) qs.set("endDate", this.endDate);

      return qs;
    },

    async fetchLogs() {
      // Latest-wins: overlapping responses resolve in arbitrary order; only
      // the newest request may write the list/pagination/spinner.
      const seq = ++this.loadSeq;
      this.isLoading = true;
      try {
        const qs = this.buildQuery();
        const response = await http.get(`/callback-logs?${qs.toString()}`);
        if (seq !== this.loadSeq) return;
        if (response.status === 200) {
          this.logs = response.data.data || [];
          this.paginatorInfo = response.data.pagination || {};
        } else {
          this.error = "Failed to fetch logs";
        }
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.message) ||
                    (error && error.message) || "Something went wrong";
        if (seq === this.loadSeq) this.error = msg;
      } finally {
        if (seq === this.loadSeq) this.isLoading = false;
      }
    },

    clearFilters() {
      this.searchKeyWord = "";
      this.refIdSearch = "";
      this.requestIdSearch = "";
      this.merchantOrderIdSearch = "";
      this.receiptIdSearch = "";
      this.clientNameFilter = "";
      this.typeFilter = "";
      this.triggeredByFilter = "";
      this.signedFilter = "";
      this.statusFilter = "";
      this.startDate = "";
      this.endDate = "";
      this.page = 1;
    },

    openDetail(log) { this.detailLog = log; },
    closeDetail() { this.detailLog = null; },

    openSignatureDebug(log) { this.signatureDebug = log; },
    closeSignatureDebug() { this.signatureDebug = null; },

    signedStringFor(log) {
      if (!log || !log.request_id || !log.signed_at_epoch) return "—";
      let bodyStr = "";
      if (log.request_payload !== null && log.request_payload !== undefined) {
        bodyStr = typeof log.request_payload === "string"
          ? log.request_payload
          : JSON.stringify(log.request_payload);
      }
      return `${log.signed_at_epoch}.${log.request_id}.${bodyStr}`;
    },

    handleError() { this.error = null; },
    handleBaseToast() { this.baseToast = { type: "", message: "" }; },

    truncate(str, max = 50) {
      if (!str) return "—";
      const s = String(str);
      return s.length > max ? s.substring(0, max) + "..." : s;
    },
    // Renamed from statusBadgeClass — same map, just returns modern pill
    // class names instead of raw color utilities.
    statusPillClass(status) {
      if (status === "success") return "pill-success";
      if (status === "failed") return "pill-danger";
      if (status === "pending") return "pill-warn";
      return "pill-neutral";
    },
    // Kept for external callers (if any). Delegates to the new class fn.
    statusBadgeClass(status) { return this.statusPillClass(status); },
    formatDate(dt) {
      if (!dt) return "—";
      const tz = process.env.VUE_APP_TIMEZONE || "Asia/Kolkata";
      return moment.tz(dt, tz).format("YYYY-MM-DD HH:mm:ss");
    },
    prettyJson(v) {
      if (v == null || v === "") return "—";
      if (typeof v === "string") {
        try { return JSON.stringify(JSON.parse(v), null, 2); }
        catch { return v; }
      }
      try { return JSON.stringify(v, null, 2); }
      catch { return String(v); }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text || "");
        this.baseToast = { type: "success", message: "Copied to clipboard" };
        setTimeout(() => this.handleBaseToast(), 1600);
      } catch (e) {
        this.baseToast = { type: "error", message: "Copy failed" };
        setTimeout(() => this.handleBaseToast(), 1600);
      }
    },

    openResendDialog(log) {
      this.resendForm.logId = log.id;
      this.resendForm.callbackUrl = log.callback_url || "";
      this.resendForm.payloadText = this.prettyJson(log.request_payload);
      this.resendForm.payloadError = null;
      this.resendForm.responseStatus = null;
      this.resendForm.responseBody = null;
      this.showResendDialog = true;
    },
    closeResendDialog() {
      this.showResendDialog = false;
      this.resendForm = {
        logId: null,
        callbackUrl: "",
        payloadText: "",
        payloadError: null,
        responseStatus: null,
        responseBody: null,
        isSending: false,
      };
    },
    async submitResend() {
      this.resendForm.payloadError = null;
      let payload;
      try { payload = JSON.parse(this.resendForm.payloadText); }
      catch (e) { this.resendForm.payloadError = `Invalid JSON: ${e.message}`; return; }

      this.resendForm.isSending = true;
      try {
        const response = await http.post(`/callback-logs/${this.resendForm.logId}/resend`, {
          callbackUrl: this.resendForm.callbackUrl,
          requestPayload: payload,
        });
        if (response.status === 200) {
          this.resendForm.responseStatus = response.data.responseStatus;
          this.resendForm.responseBody = JSON.stringify(response.data.responseBody, null, 2);
          this.baseToast = { type: "success", message: "Callback resent successfully" };
          setTimeout(() => this.handleBaseToast(), 1600);
          setTimeout(() => { this.fetchLogs(); }, 1000);
        } else {
          this.error = response.data.message || "Failed to resend callback";
        }
      } catch (error) {
        this.resendForm.responseStatus = error.response?.status;
        this.resendForm.responseBody = error.response?.data?.message || error.message || "Error sending callback";
      }
      this.resendForm.isSending = false;
    },

    // XLSX export — same client-side pattern as the pay-in list. Hits
    // the list endpoint with `first=EXPORT_CAP` (backend clamps to the
    // same ceiling), formats + truncates the columns, then hands the
    // XLSX bundle to the browser as a download. No new backend endpoint
    // needed.
    async exportData() {
      this.isExporting = true;
      try {
        const qs = this.buildQuery({ page: 1, first: EXPORT_CAP });
        const response = await http.get(`/callback-logs?${qs.toString()}`);
        if (response.status !== 200) throw new Error("Server returned " + response.status);
        const rows = (response.data && response.data.data) || [];
        if (rows.length === 0) {
          this.$swal ? this.$swal.fire({ icon: "info", title: "Nothing to export", text: "No callback logs match the current filters." })
                     : alert("Nothing to export");
          return;
        }

        const clip = (v) => {
          if (v == null) return "";
          const s = typeof v === "string" ? v : JSON.stringify(v);
          return s.length > EXPORT_CELL_MAX ? s.substring(0, EXPORT_CELL_MAX) + "…[truncated]" : s;
        };
        const fmt = (v) => String(v || "").replace("T", " ").replace(/\.\d+Z?$/, "").replace(/Z$/, "");

        const data = rows.map(item => ({
          "ID": item.id,
          "Sent At (IST)": fmt(item.created_at),
          "Status": item.status || "",
          "HTTP Code": item.response_status != null ? item.response_status : "",
          "Type": item.callback_type || "",
          "Triggered By": item.triggered_by || "",
          "Attempt": item.attempt_number || 1,
          "Signed": item.signed === 1 || item.signed === true ? "Yes" : "No",
          "Client": item.client_name || "",
          "Vendor": item.vendor || "",
          "Ref ID": item.ref_id || "",
          "Request ID": item.request_id || "",
          "Merchant Order ID": item.merchant_order_id || "",
          "Receipt ID": item.receipt_id || "",
          "Callback URL": item.callback_url || "",
          "Signed At (epoch)": item.signed_at_epoch || "",
          "Signature (sha256=…)": item.signature ? `sha256=${item.signature}` : "",
          "Request Payload": clip(item.request_payload),
          "Response Body": clip(item.response_body),
          "Error Message": item.error_message || "",
          "Parent Log ID": item.parent_log_id || "",
        }));

        const xlsxMod = await import("xlsx/dist/xlsx.full.min.js");
        const XLSX = xlsxMod.default || xlsxMod;
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CallbackLogs");
        const istDate = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
        XLSX.writeFile(wb, `callback-logs-${istDate}.xlsx`);

        if (rows.length >= EXPORT_CAP) {
          this.$swal
            ? this.$swal.fire({
                icon: "warning",
                title: "Export truncated",
                text: `Only the first ${EXPORT_CAP} rows were exported. Narrow your filters to capture the rest.`,
              })
            : alert(`Export truncated at ${EXPORT_CAP} rows.`);
        }
      } catch (error) {
        const msg =
          (error && error.response && error.response.data && error.response.data.message) ||
          (error && error.message) ||
          "Export failed";
        this.error = msg;
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.cbl-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 16px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.cbl-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.cbl-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}
.header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-sm { padding: 8px 14px; font-size: 12px; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.btn-export {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}
.btn-export:hover:not(:disabled) {
  background: #d1fae5;
  border-color: #6ee7b7;
}
.btn-icon { width: 15px; height: 15px; }
.btn-spinner {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
.btn-spinner-dark {
  border-color: rgba(4, 120, 87, 0.25);
  border-top-color: #047857;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Small in-modal link-style button */
.link-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #1B2A47;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
}
.link-btn svg { width: 13px; height: 13px; }
.link-btn:hover { background: #eef2f7; border-color: #cbd5e1; }
.link-btn-danger { color: #b91c1c; }
.link-btn-danger:hover { background: #fef2f2; border-color: #fecaca; }

/* ==================== Filter bar ==================== */
.cbl-filters {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 240px;
  display: flex;
  align-items: center;
}
.search-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.search-input {
  width: 100%;
  padding: 9px 34px 9px 36px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s;
  color: #0f172a;
  font-family: inherit;
}
.search-input:focus { border-color: #1B2A47; background: #fff; }
.search-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.search-clear:hover { background: #f1f5f9; color: #475569; }
.search-clear svg { width: 14px; height: 14px; }

/* Chips */
.chip-group { display: flex; gap: 4px; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
  font-family: inherit;
}
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-icon { width: 13px; height: 13px; }
/* Active state styling per status. Kept as multiple selectors so the
   colored active look is legible even against the near-white filter bar
   background. */
.chip-active { font-weight: 700; }
.chip-active-all       { background: #f1f5f9; border-color: #cbd5e1; color: #0f172a; }
.chip-active-success   { background: #d1fae5; border-color: #6ee7b7; color: #065f46; }
.chip-active-failed    { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
.chip-active-pending   { background: #fef3c7; border-color: #fcd34d; color: #92400e; }
.chip-active-primary   {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 12px -4px rgba(27, 42, 71, 0.45);
}
/* Advanced chip: indigo text when idle, but let .chip-active-primary win
   when it's toggled on (otherwise indigo text sits on the indigo gradient
   bg and disappears). */
.chip-advanced:not(.chip-active) { color: #1B2A47; }

/* ==================== Advanced filters panel ==================== */
.cbl-advanced {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.adv-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.adv-title {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.adv-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 14px;
}
@media (max-width: 960px) { .adv-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .adv-grid { grid-template-columns: 1fr; } }
.cbl-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.cbl-field label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.cbl-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  background: #f8fafc;
  outline: none;
  color: #0f172a;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
}
.cbl-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
select.cbl-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
}
.adv-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
}

/* Date pill (matches Upload History filter row) */
.date-pill {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.date-pill:focus-within {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.date-pill-label {
  padding: 7px 10px;
  font-size: 10px;
  color: #64748b;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border-right: 1px solid #eef2f7;
}
.date-pill-input {
  padding: 7px 10px;
  border: none;
  background: transparent;
  font-size: 12px;
  color: #0f172a;
  outline: none;
  font-family: inherit;
  min-width: 120px;
}

/* ==================== Card + Table ==================== */
.cbl-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.head-count {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
}
.card-pag-info {
  font-size: 11px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
}
.cbl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cbl-table thead { background: #f8fafc; }
.cbl-table th {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.th-actions { text-align: right; }
.cbl-table tbody tr {
  transition: background 0.12s;
}
.cbl-table tbody tr:nth-child(even) { background: #fafbfc; }
.clickable-row { cursor: pointer; }
.clickable-row:hover { background: #eef2f7 !important; }
.cbl-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  vertical-align: middle;
  white-space: nowrap;
}
.cbl-table tbody tr:last-child td { border-bottom: none; }
.td-num { font-variant-numeric: tabular-nums; }
.td-mini { font-size: 11px; color: #64748b; }
.td-mono {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 11px;
  color: #475569;
}
.td-clip { max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
.td-http { font-weight: 700; font-variant-numeric: tabular-nums; }
.td-actions {
  text-align: right;
  white-space: nowrap;
}
.mono-link {
  color: #1B2A47;
  cursor: pointer;
}
.mono-link:hover { text-decoration: underline; }

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  color: #475569;
  transition: all 0.12s;
}
.action-btn svg { width: 13px; height: 13px; }
.action-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.action-resend:hover { color: #1B2A47; border-color: #cbd5e1; background: #eef2f7; }

/* Pills */
.pill {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pill-success { background: #d1fae5; color: #065f46; }
.pill-danger  { background: #fee2e2; color: #991b1b; }
.pill-warn    { background: #fef3c7; color: #92400e; }
.pill-neutral { background: #f1f5f9; color: #64748b; }
.pill-type    { background: #eef2f7; color: #1B2A47; }

/* Signed compact badge (space-tight in the wide table) */
.signed-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px; height: 22px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
}
.signed-yes { background: #d1fae5; color: #047857; }
.signed-no  { background: #f1f5f9; color: #94a3b8; }

/* Empty / loading */
.empty-hero {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}
.empty-hero-icon {
  width: 48px; height: 48px;
  border-radius: 12px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
}
.empty-hero-icon svg { width: 24px; height: 24px; }
.empty-hero-title { font-size: 14px; font-weight: 600; color: #0f172a; }
.empty-hero-sub { font-size: 12px; color: #64748b; }

.cbl-loading {
  padding: 50px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.loading-spinner {
  width: 40px; height: 40px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
.loading-label { font-size: 13px; color: #64748b; }

.pag-bar { margin-top: 4px; }

/* ==================== Toast ==================== */
.cbl-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.cbl-toast .dot {
  width: 8px; height: 8px; border-radius: 999px; background: #94a3b8;
}
.cbl-toast.is-success { background: #059669; }
.cbl-toast.is-success .dot { background: #a7f3d0; }
.cbl-toast.is-error { background: #dc2626; }
.cbl-toast.is-error .dot { background: #fecaca; }
.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }

/* ==================== Custom teleported modal (Signature Debug) ==================== */
.cbl-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 50;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.cbl-modal {
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px -20px rgba(15, 23, 42, 0.4);
}
.cbl-modal-md { max-width: 640px; }
.cbl-modal-head {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
}
.cbl-modal-eyebrow {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 700;
}
.cbl-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}
.cbl-modal-close {
  border: none; background: transparent; cursor: pointer;
  width: 32px; height: 32px; border-radius: 8px; color: #94a3b8;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.12s;
}
.cbl-modal-close:hover { background: #f1f5f9; color: #475569; }
.cbl-modal-close svg { width: 18px; height: 18px; }
.cbl-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.cbl-modal-foot {
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Modal transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.18s ease; }
.modal-fade-enter-active .cbl-modal, .modal-fade-leave-active .cbl-modal {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-from .cbl-modal, .modal-fade-leave-to .cbl-modal {
  transform: translateY(20px) scale(0.98);
}

/* Sig-status pill + field blocks */
.sig-status-row { display: flex; }
.pill-lg {
  padding: 6px 14px;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.pill-lg svg { width: 12px; height: 12px; }

.sig-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.sig-field {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sig-field-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.sig-field-name {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.sig-field-header {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 10px;
  color: #94a3b8;
  background: #eef2f7;
  padding: 1px 6px;
  border-radius: 4px;
}
.sig-field-value {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mono-value {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  color: #0f172a;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}
.mini-copy {
  border: 1px solid transparent;
  background: transparent;
  color: #94a3b8;
  width: 26px; height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
}
.mini-copy:hover {
  color: #1B2A47;
  background: #eef2f7;
  border-color: #cbd5e1;
}
.mini-copy svg { width: 13px; height: 13px; }
.mini-copy-inline {
  width: auto;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
  margin-left: auto;
}

.sig-block-full {
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sig-block-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.sig-block-title {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.sig-block-hint {
  font-size: 11px;
  color: #94a3b8;
}
.sig-signed-string {
  width: 100%;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  background: #0f172a;
  color: #94a3b8;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #1e293b;
  outline: none;
  resize: vertical;
  min-height: 110px;
  line-height: 1.5;
}
.sig-verify-hint {
  font-size: 11px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}
.sig-verify-hint code {
  background: #eef2f7;
  color: #1B2A47;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 10.5px;
}

/* ==================== Modal content (base-dialog + base-modal wrap) ==================== */
.modal-body-inline {
  padding: 6px 4px 10px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-size: 13px;
}
.kv { display: flex; flex-direction: column; gap: 5px; }
.kv-label {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.kv-hint {
  color: #94a3b8;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  margin-left: 6px;
  font-size: 11px;
}
.kv-mono {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 8px 10px;
  border-radius: 8px;
  color: #0f172a;
  word-break: break-all;
}
.kv-textarea {
  width: 100%;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 8px 10px;
  border-radius: 8px;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.kv-textarea:focus { border-color: #1B2A47; background: #fff; box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14); }
.kv-textarea-lg { min-height: 140px; }
.kv-hint-block {
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
}
.kv-hint-block code {
  background: #f1f5f9;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Menlo, monospace;
  color: #1B2A47;
  font-size: 11px;
}
.kv-error {
  font-size: 12px;
  color: #dc2626;
  margin: 0;
  font-weight: 500;
}

.resp-block {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  max-height: 200px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.resp-status {
  font-size: 12px;
  color: #475569;
}
.resp-status b {
  color: #0f172a;
  font-family: 'SFMono-Regular', Menlo, monospace;
}

/* Detail modal */
.detail-modal {
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 13px;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 16px;
}
.detail-grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
@media (max-width: 700px) {
  .detail-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .detail-grid-2 { grid-template-columns: 1fr; }
}
.detail-cell { min-width: 0; }
.cell-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 3px;
}
.cell-value {
  font-size: 13px;
  color: #0f172a;
}
.cell-strong { font-weight: 700; }
.cell-mono {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 11px;
  color: #475569;
  word-break: break-all;
}
.cell-mono-block {
  background: #f1f5f9;
  padding: 8px 10px;
  border-radius: 8px;
  margin-top: 4px;
}

.detail-section {
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.detail-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.code-block {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 11px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 12px;
  border-radius: 10px;
  max-height: 260px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  line-height: 1.5;
}
.code-block-error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}
.sig-block {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 11px;
  background: #f1f5f9;
  padding: 10px 12px;
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.sig-key { color: #64748b; margin-right: 4px; }
.break-all { word-break: break-all; }

.detail-foot {
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1); }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
